import { type CSSProperties, type ReactElement, useEffect, useMemo, useState } from "react"

import { type FileRejection, useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import { Grid } from "@mui/material"
import { Button, Tag, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { getTheme } from "../../hooks/UseTheme"
import { fileExtensionValidator, formatFileSize } from "../../utils/genericHelpers"
import { LinearProgressWithLabel } from "../status/LinearProgressWithLabel"

import styles from "./Dropzone.module.scss"

type DropzoneProps = {
  uploadingCSV?: boolean
  uploadProgress?: number
  resetProgress?: () => void
  selectedFiles?: File[]
  setSelectedFiles: (file: File[]) => void
  setInvalidFiles?: (file: FileRejection[]) => void
  bottomText?: string
  handleCancelUpload?: () => void
  onDropZoneOpen?: (open: () => void) => void
  titleHeader?: {
    title?: string
    tooltip?: string
  }
  description?: string
  extensions?: Record<string, Array<string>>
  maxSize?: number
}

/**
 * dropZone component
 * @param  {boolean} uploadingCSV
 * @param  {number} uploadProgress
 * @param  {File[]} selectedFiles
 * @param  {Function} setSelectedFiles
 * @param  {Function} setInvalidFiles
 * @param  {string} bottomText text to display at the bottom of the dropzone
 * @param  {Function} handleCancelUpload
 * @return  {ReactElement}
 */
export function Dropzone(props: Readonly<DropzoneProps>): ReactElement {
  const { t } = useTranslation("form", {
    keyPrefix: "dropzone",
  })

  const {
    uploadingCSV,
    uploadProgress,
    resetProgress,
    selectedFiles,
    setSelectedFiles,
    setInvalidFiles,
    bottomText,
    handleCancelUpload,
    onDropZoneOpen,
    titleHeader,
    description = t("dropzoneAreaDescription"),
    extensions = { "text/*": [".csv"] },
    maxSize,
  } = props

  const theme = getTheme()

  const [uploadTime, setUploadTime] = useState<Date | null>(null)

  const baseStyle: CSSProperties = useMemo(() => {
    return {
      flex: 1,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "20px",
      borderWidth: 2,
      borderRadius: 4,
      borderColor: theme.palette.secondary.border.default,
      borderStyle: "dashed",
      backgroundColor: theme.palette.secondary.background.default,
      color: theme.palette.base.text[2],
      outline: "none",
      transition: "border .24s ease-in-out",
      cursor: "pointer",
    }
  }, [theme.palette.base.text, theme.palette.secondary.background.default, theme.palette.secondary.border.default])

  const activeStyle = useMemo(() => {
    return {
      borderColor: theme.palette.blue.border,
    }
  }, [theme.palette.blue.border])

  const acceptStyle = useMemo(() => {
    return {
      borderColor: theme.palette.green.border,
    }
  }, [theme.palette.green.border])

  const rejectStyle = useMemo(() => {
    return {
      borderColor: theme.palette.red.border,
    }
  }, [theme.palette.red.border])

  const onDrop = (acceptedFiles: File[]): void => {
    acceptedFiles.forEach((file: File) => {
      const reader = new FileReader()
      reader.readAsText(file)
    })
  }

  // Dropzone stuff
  const { fileRejections, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    disabled: uploadingCSV,
    multiple: false,
    onDrop,
    accept: { ...extensions },
    validator: (file) => fileExtensionValidator(file, Object.values(extensions).flat()),
    onDropAccepted: (files) => {
      setUploadTime(new Date())
      setSelectedFiles(files)
    },
    onDropRejected: (files) => {
      setInvalidFiles?.(files)
    },
    maxSize,
  })

  // Need to style dropzone, https://react-dropzone.js.org/#!/Styling%20Dropzone
  const dropzoneStyle = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [baseStyle, isDragActive, activeStyle, isDragAccept, acceptStyle, isDragReject, rejectStyle],
  )

  useEffect(() => {
    if (!uploadingCSV && uploadProgress === 100) {
      setUploadTime(new Date())
    }
  }, [uploadingCSV, uploadProgress])

  // this effect open files if the passed `onDropZoneOpen` is passed and called
  useEffect(() => {
    if (onDropZoneOpen) {
      onDropZoneOpen(open)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Grid item container className={styles.root}>
      {(titleHeader?.title || titleHeader?.tooltip) && (
        <Typography variant="label-bold" tooltip={titleHeader?.tooltip} tooltipVerticalAlign="middle">
          {titleHeader?.title}
        </Typography>
      )}

      {/* add && uploadingCSV */}
      {selectedFiles && selectedFiles.length > 0 && !(fileRejections && fileRejections.length > 0) ? (
        <Grid item container className={styles.dropzone}>
          <Grid item container gap={0.5}>
            <Typography variant="p">{selectedFiles[0]?.name}</Typography>
            <Tag color="blue" size="xsmall">
              {selectedFiles[0]?.name.split(".").pop()?.toUpperCase()}
            </Tag>
          </Grid>

          {(uploadingCSV || Boolean(uploadProgress && uploadProgress > 0)) && (
            <Typography variant="span-regular" variantColor={2}>
              {uploadingCSV || (uploadProgress && uploadProgress > 0 && uploadProgress < 100)
                ? t("uploadingFileStatus")
                : `${t("uploadedFileStatus")} : ${moment(uploadTime).format("h:mm A")} . ${formatFileSize(selectedFiles[0]?.size ?? 0)}`}
            </Typography>
          )}

          <Grid item container alignItems={"baseline"} gap={2}>
            {(uploadingCSV || Boolean(uploadProgress && uploadProgress > 0)) && (
              <Grid item xs>
                <LinearProgressWithLabel value={uploadProgress ?? 0} isUploading={uploadingCSV ?? false} />
              </Grid>
            )}

            <Button
              variant="secondary"
              size="small"
              onClick={
                uploadingCSV
                  ? handleCancelUpload
                  : () => {
                      setSelectedFiles([])
                      resetProgress && resetProgress()
                    }
              }
            >
              {uploadingCSV ? t("cancel") : t("reupload")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <div {...getRootProps({ style: dropzoneStyle })}>
          <input {...getInputProps()} />

          <FileUploadOutlinedIcon
            sx={{
              fontSize: "1.25rem",
              verticalAlign: "bottom",
              color: "var(--secondary-icon-base)",
            }}
          />
          <Typography variant="label-regular"> {description}</Typography>
        </div>
      )}

      {bottomText && (!selectedFiles || selectedFiles.length <= 0) && (
        <Typography variant="label-regular" variantColor={2}>
          {bottomText}
        </Typography>
      )}
    </Grid>
  )
}
